import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { images } from '../utils/images';
import { CheckCircle2, Phone, Heart, Users, Shield, MessageCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyCallButton from '../components/StickyCallButton';
import { Button } from '../components/ui/button';
import VideoBackground from '../components/VideoBackground';
import SEO from '../components/SEO';

const CounsellingNdisPage = () => {
  const faqs = [
    {
      question: "What types of counselling are available under the NDIS?",
      answer: "The NDIS funds a range of counselling services including psychological therapy, behavioural therapy, cognitive behavioural therapy (CBT), and trauma-informed counselling. Services can be delivered individually, in groups, or via telehealth depending on your needs and preferences."
    },
    {
      question: "Who is eligible for NDIS-funded counselling?",
      answer: "NDIS participants whose plans include Capacity Building or Improved Daily Living supports may be eligible for funded counselling. Your plan must specify therapeutic supports, and the counselling must be related to your disability and goals."
    },
    {
      question: "How do I get counselling included in my NDIS plan?",
      answer: "Speak with your Local Area Coordinator or NDIA planner during your plan review. Provide evidence from your GP or specialist about how counselling supports your disability-related goals. Our team can help you prepare the right documentation."
    },
    {
      question: "Can children and adolescents access NDIS counselling?",
      answer: "Yes, children and adolescents with disability-related psychosocial needs can access counselling through their NDIS plan. We offer age-appropriate approaches that help young people build resilience, manage emotions, and develop social skills."
    },
    {
      question: "How often can I attend counselling sessions?",
      answer: "Session frequency depends on your NDIS plan funding and your individual needs. Some participants attend weekly, while others may benefit from fortnightly or monthly sessions. Your counsellor will work with you to determine the best schedule."
    },
    {
      question: "Is telehealth counselling available?",
      answer: "Yes, we offer telehealth counselling for participants who prefer remote sessions or have difficulty attending in person. Telehealth sessions are delivered via secure video platforms and are just as effective as face-to-face sessions."
    }
  ];

  const scrollToContact = () => { window.location.href = '/contact'; };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Counselling On The NDIS" description="Professional NDIS-funded counselling services in Brisbane. Individual, family, youth and trauma-informed counselling to support your mental health and well-being." />
      <Header />

      <section className="relative pt-32 pb-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" style={{ color: '#587740' }} data-testid="counselling-ndis-title">
                Counselling On The NDIS
              </h1>
              <p className="text-lg md:text-xl leading-relaxed mb-4" style={{ color: '#5e5e5e' }}>
                Living with emotional pain, trauma, or mental health challenges can feel incredibly isolating. You don't have to carry that weight alone. Our counsellors are warm, experienced professionals who genuinely care about your well-being — and they'll meet you exactly where you are.
              </p>
              <p className="text-lg leading-relaxed mb-4" style={{ color: '#5e5e5e' }}>
                In simple terms — we provide a safe, judgement-free space where you can talk openly, process difficult feelings, and develop practical strategies to feel better. All funded through your NDIS plan.
              </p>
              <p className="text-base leading-relaxed italic mb-4" style={{ color: '#6c935e' }}>
                We see the psychological struggles that go unseen. You are heard and valued and in control.
              </p>
              <p className="text-sm font-semibold mb-8" style={{ color: '#587740' }}>
                Book a free consultation — we'll review your situation and create a personalised action plan at no cost.
              </p>
              <Button onClick={scrollToContact} size="lg" className="text-white hover:opacity-90" style={{ backgroundColor: '#81b956' }} data-testid="counselling-hero-cta">
                <Phone className="w-5 h-5 mr-2" /> Contact Us
              </Button>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img src={images.mentalHealth1} alt="Counselling services" className="w-full h-[400px] md:h-[500px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-center" style={{ color: '#587740' }}>
            How NDIS Counselling Can Help
          </h2>
          <p className="text-lg text-center max-w-4xl mx-auto mb-12" style={{ color: '#5e5e5e' }}>
            Counselling under the NDIS is designed to help you manage your mental health, learn practical coping strategies, and build the emotional strength to live the life you want. Put simply — it's about giving you the tools and support to feel more in control.
          </p>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg leading-relaxed mb-4" style={{ color: '#5e5e5e' }}>
                Our qualified counsellors work with you to address challenges like anxiety, depression, trauma, grief, and the emotional toll of living with a disability. We don't take a one-size-fits-all approach — every session is shaped around you, your story, and what you need right now.
              </p>
              <p className="text-lg leading-relaxed" style={{ color: '#5e5e5e' }}>
                Whether you need short-term support to get through a tough time, or ongoing therapy to build lasting resilience, our team walks alongside you with patience and genuine care. Many other providers rush through sessions — we don't. Your time with us is your time.
              </p>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img src={images.mentalHealth2} alt="Supportive counselling environment" className="w-full h-[350px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" style={{ color: '#587740' }}>
            Our Counselling Services
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Heart, title: "Individual Counselling", desc: "One-on-one sessions focused on your personal goals, helping you manage emotions, build coping skills, and work through challenges at your own pace." },
              { icon: Users, title: "Family & Group Support", desc: "Sessions that include family members or group settings to build communication skills, strengthen relationships, and create a supportive network." },
              { icon: Shield, title: "Trauma-Informed Care", desc: "Specialised support for individuals who have experienced trauma, delivered with sensitivity, safety, and a deep understanding of recovery." },
              { icon: MessageCircle, title: "Youth Counselling", desc: "Age-appropriate counselling for children and adolescents, helping young people navigate emotions, social challenges, and the transition to independence." },
              { icon: Phone, title: "Telehealth Sessions", desc: "Convenient remote counselling via secure video, making it easier to access support from the comfort of your home." },
              { icon: CheckCircle2, title: "Crisis Support", desc: "Responsive support during difficult times, helping you manage acute situations and develop strategies to prevent future crises." }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <Card key={i} className="border-none shadow-lg hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#AABC5A' }}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-3" style={{ color: '#587740' }}>{item.title}</h3>
                    <p className="text-base leading-relaxed" style={{ color: '#5e5e5e' }}>{item.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="counselling-faq">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-12 text-center" style={{ color: '#587740' }}>
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border rounded-lg px-6 shadow-sm" data-testid={`counselling-faq-${i}`}>
                <AccordionTrigger className="text-left hover:no-underline py-6" style={{ color: '#587740' }}>
                  <span className="text-lg font-semibold pr-4">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed pb-6" style={{ color: '#5e5e5e' }}>{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-12">
            <Button onClick={scrollToContact} size="lg" className="text-white hover:opacity-90" style={{ backgroundColor: '#81b956' }}>
              <Phone className="w-5 h-5 mr-2" /> Contact Us
            </Button>
          </div>
        </div>
      </section>

      <VideoBackground variant="dark" className="py-20">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">You Deserve to Feel Better — Let's Start That Journey Together</h2>
          <p className="text-lg mb-8 text-white/90">Our compassionate team is here to listen, understand, and help you build the emotional resilience you need. Free consultation, free action plan — just a genuine conversation about how we can help. No pressure, no obligation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="tel:+611300404229">
            <Button size="lg" className="text-base px-8 py-6" style={{ backgroundColor: '#FFFFFF', color: '#587740' }}>
              <Phone className="w-5 h-5 mr-2" /> Call Us Now
            </Button>
          </a>
          <a href="/get-on-ndis-free">
            <Button size="lg" variant="outline" className="text-base px-8 py-6 border-2 border-white text-white hover:bg-white/10">
              Get on the NDIS Free
            </Button>
          </a>
          </div>
        </div>
      </VideoBackground>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default CounsellingNdisPage;
